/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectors } from 'farmx-redux-core';
//@ts-ignore
import { BlockSelect } from 'farmx-web-ui';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { Form, notification, Alert } from 'antd';
import {
  useRenderReusableControls, useUpdateFormFields, useHandlers,
  useSetActionType, getDefaultTimes, checkIsValidDate,
  autonomousTypes, checkIsRuleExists
} from 'helper/reusableControls';
import { isMobile } from 'utils/detectDevice';
import { FORM_RULES } from '../../../constants';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import AutonomousIrrigationControls from './AutonomousIrrigationControls';
import AutonomousSprinklerControls from './AutonomousSprinklerControls';
import AutonomousDefaultControls from './AutonomousDefaultControls';
import RecuringControls from './RecurringControls';
import ThresholdSettings from '../../mobile/settings/ThresholdSettings';
import SensorControl from './SensorControl';
import './ruleSettingsPage.less';

interface AutonomousControlProps {
  isEdit?: boolean;
  blocks: object[];
  renderItems?: string[];
  showDefaultControls?: boolean;
  settings?: any;
  onUpdate?: any;
  formRef?: any;
  onCancel: any;
  loading: boolean;
  configList?: any;
}

const { selectAllCoordination, selectSelectedRanch } = selectors;

const AutonomousControl: React.FC<AutonomousControlProps> = (props: any) => {
  const {
    isEdit, renderItems, showDefaultControls, settings, onUpdate, formRef,
    onCancel, blocks, loading, configList,
  } = props;
  const { t } = useTranslation();
  const location = useLocation();
  const [form] = Form.useForm();
  const rules = FORM_RULES(t);
  const { requiredBlockField } = rules;
  const [updatedInitialValues, setUpdatedInitialValues] = useState<any>({});
  const [autonomousMethod, setAutonomousMethod] = useState('irrigation');
  const [selectedBlock, setSelectedBlock] = useState<any>(null);
  const [selectedSensor, setSelectedSensor] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const { state } = location || {};
  const isTimeShiftIrrigation = autonomousMethod === 'time_shift_irrigation'
    || autonomousMethod === 'time_shift_sprinkler_irrigation';
  const prefix = isTimeShiftIrrigation ? 'source_': '';
  const { startTime, endTime } = getDefaultTimes(undefined, undefined,
    { hour: 6, minute: 30 }, { hour: 6, minute: 40 });
  const now = isTimeShiftIrrigation ? startTime : moment();
  const endDate = isTimeShiftIrrigation ? endTime : moment(now).add(2, 'hours');
  const { onFinish } = useHandlers(updatedInitialValues, isEdit, onUpdate, prefix);
  const { renderButton, renderSelect } = useRenderReusableControls(t);
  const [selectedRanchId] = (useSelector(selectSelectedRanch) as {payload: Array<any>})?.payload || [];

  const autonomousMethods = [
    // { value: 'unknown', label: 'Unknown' }, // This will be enabled in future
    { value: 'irrigation', label: t('Drip Irrigation') },
    { value: 'sprinkler', label: t('Sprinkler Irrigation') },
    { value: 'cooling', label: t('Sprinkler Cooling') },
    { value: 'time_shift_irrigation', label: t('Time Shifted Drip Irrigation') },
    { value: 'time_shift_sprinkler_irrigation', label: t('Time Shifted Sprinkler Irrigation') },
  ];
  /* eslint-disable no-template-curly-in-string */
  const validateMessages = {
    required: t('${label} is required!'),
  };
  // This will be removed
  // Get autonomous irrigation settings config
  // const allConfig = useSelector((state) => selectAllCoordination(state)) || [];

  // To set the default autonomous method based on the config
  // const {
  //   isDripIrrigation,
  //   isSprinklerCooling, isSprinklerIrrigation
  // } = useSetActionType(allConfig, selectedBlock, form, setAutonomousMethod);

  // To update the Form with initial value
  const autoFillValues = settings || state;

  useUpdateFormFields(autoFillValues, form, now, endDate, autonomousMethod,
    setUpdatedInitialValues, setSelectedBlock, setSelectedSensor, setAutonomousMethod, prefix, isEdit);

  const onChange = (value: any) => {
    setAutonomousMethod(value);
    setShowWarning(false);
    setWarningMessage('');
    form.setFieldsValue({ action_type: value });
    form.setFieldsValue({ depth: undefined });
    // Reset sensor selection when autonomous menthod changes
    if (form.getFieldValue('sensor')) {
      form.setFieldsValue({ sensor: undefined });
      setSelectedSensor(null);
    }
  };

  const onDelete = () => {
    setShowModal(true);
  };

  useEffect(() => {
    setIsRecurring(updatedInitialValues?.is_recurring);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedInitialValues?.is_recurring]);

  useEffect(() => {
    if (formRef) formRef(form);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);
  
  // To validate the own block date range
  const onDateChange = () => {
    const {
      [`${prefix}starting_time_window`]: sourceStartingTimeWindow,
      [`${prefix}ending_time_window`]: sourceEndingTimeWindow,
      starting_time_window, ending_time_window,
    } = form.getFieldsValue() || {};

    const { isValid, message } = checkIsValidDate(starting_time_window, ending_time_window,
      sourceStartingTimeWindow, sourceEndingTimeWindow, t)
    if (!isValid) {
      notification.config({ maxCount: 1 });
      notification.error({ message })
      setShowWarning(true);
      setWarningMessage(message);
    } else if (isValid) {
      const isOverlapping = checkIsRuleExistsWithTimeWindow();      
      if (!isOverlapping) setShowWarning(false);
    }
  }

  const onToggleChange = (d: boolean) => { setIsRecurring(d); };

  function checkIsRuleExistsWithTimeWindow() {
    return checkIsRuleExists(form, showWarning, configList, isEdit,
      setWarningMessage, setShowWarning);
  }

  // const fixedButtonPosition = isRecurring ? 'form-action-buttons' : '';

  // Check and show error message if rule exists
  useEffect(() => {
    if (isEdit) return;
  
    if (showWarning) {
      notification.config({ maxCount: 1 });
      notification.error({ message: t(warningMessage)})
    }
  }, [isEdit, showWarning, warningMessage]);

  return (
    <div className={`rule-setting-form width-auto ${isMobile ? 'width-100-percent' : ''} margin-inherit`}>
      <Form
        form={form}
        layout="vertical"
        name="Rule Settings"
        onFinish={(d) => onFinish(d)}
        validateMessages={validateMessages}
        initialValues={updatedInitialValues}
        onFieldsChange={() => {
          checkIsRuleExistsWithTimeWindow();
        }}
      >
        <Form.Item className="font-bold" name="block" label={t('Block')} rules={requiredBlockField}>
          <BlockSelect onSelect={(d: any) => setSelectedBlock(d)} items={blocks} />
        </Form.Item>
        <Form.Item
          className="font-bold form-item-width-100"
          name="action_type"
          label={t('Autonomous Methods')}
        >
          {renderSelect(autonomousMethods, 'Select Type', autonomousMethod, onChange)}
        </Form.Item>

        {showDefaultControls && (
          <AutonomousDefaultControls
            startLabel={isTimeShiftIrrigation ? 'Irrigation Start Time At Destination' : undefined}
            endLabel={isTimeShiftIrrigation ? 'Irrigation End Time At Destination' : undefined}
            showDefaultDates={isTimeShiftIrrigation}
            sTime={isTimeShiftIrrigation ? { hour: 6, minute: 30 } : {}}
            eTime={isTimeShiftIrrigation ? { hour: 6, minute: 40 } : {}}
            onChange={isTimeShiftIrrigation ? onDateChange : undefined}
          />
        )}

        {(autonomousMethod === 'irrigation' || autonomousMethod === 'sprinkler')
         && (
         <AutonomousIrrigationControls
           selectedBlock={selectedBlock}
           initialValues={updatedInitialValues}
           setSelectedSensor={setSelectedSensor}
           selectedSensor={selectedSensor}
           form={form}
           autoFillValues={autoFillValues}
         />
         )}

        {autonomousMethod === 'cooling' && (
        <AutonomousSprinklerControls
          setSelectedSensor={setSelectedSensor}
          selectedSensor={selectedSensor}
          form={form}
          selectedBlock={selectedBlock}
          autoFillValues={autoFillValues}
        />
        )}

        {isTimeShiftIrrigation && (
          <>
            <SensorControl
              form={form}
              setSelectedSensor={setSelectedSensor}
              sensor={selectedSensor}
              formLabel={'Reference Soil Moisture Sensor At Destination'}
            />

            <AutonomousDefaultControls
              startLabel='Source Irrigation Start Time'
              endLabel='Source Target VWC Reset Time'
              prefix='source_'
              showDefaultDates
              sTime={{ hour: 6 }}
              eTime={{ hour: 7 }}
              onChange={isTimeShiftIrrigation ? onDateChange : undefined}
            />
            <ThresholdSettings
              controls={autoFillValues?.controls}
              selectedRanch={selectedRanchId}
              selectedBlock={selectedBlock}
              ranchBlockConstraint={false}
              irrigationFormRef={form}
              showSprinkler={false}
              showButton={false}
              isThreshold={false}
              onSubmit={onFinish}
              prefix={prefix}
              isTimeShiftIrrigation={isTimeShiftIrrigation}
              showMaxValveDurationControl={false}
            />
          </>
        )}
        <RecuringControls
          selectedBlock={selectedBlock}
          startDate={form.getFieldsValue()?.starting_time_window}
          endDate={form.getFieldsValue()?.ending_time_window}
          initialValues={updatedInitialValues}
          form={form}
          onChange={onToggleChange}
        />
        {showWarning
          && <Alert className="margin-bottom-10" message={warningMessage} type="error" />}

        {/* Button Controls  */}
        {!isEdit && (
          <div className="flex-space-between margin-15">
            <Form.Item className="font-bold form-item-width-100">
              {renderButton('Save', 'primary', 'submit', undefined, showWarning, undefined, loading)}
            </Form.Item>
            <Form.Item className="font-bold form-item-width-100">
              {renderButton('Cancel', undefined, undefined, onCancel, loading)}
            </Form.Item>
          </div>
        )}
        {isEdit && (
        <div className="flex-space-between margin-15">
          <Form.Item className="font-bold form-item-width-100">
            {renderButton('Update', 'primary', 'submit', undefined, showWarning, undefined, loading)}
          </Form.Item>
          <Form.Item className="font-bold form-item-width-100">
            {renderButton('Delete', undefined, undefined, onDelete, loading)}
          </Form.Item>
        </div>
        )}
      </Form>
      <ConfirmDeleteModal
        scheduleId={settings?.schedule?.id}
        showModal={showModal}
        setShowModal={setShowModal}
        onUpdate={onUpdate}
      />
    </div>
  );
};

AutonomousControl.defaultProps = {
  showDefaultControls: true,
  isEdit: false,
  configList: [],
};

export default AutonomousControl;
